import { Component, inject, OnInit } from '@angular/core';
import { Auth, signOut } from '@angular/fire/auth';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { UserPortalService } from '../services/user-portal.service';

@Component({
  selector: 'app-landing',
  standalone: true,
  imports: [RouterModule],
  templateUrl: './landing.component.html',
  styleUrl: './landing.component.scss'
})
export class LandingComponent implements OnInit {
  private auth = inject(Auth);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private userPortalService = inject(UserPortalService);

  sessionId: string | null = null;
  machineId: string | null = null;
  date: string | null = null;
  
  printUrl: string | null = null;
  gifUrl: string | null = null;
  cutStyle: string | null = null;
  isLoading = false;

  isClaiming = false;
  isClaimed = false;
  selectedMedia: string | null = null;

  // 48-Hour Retention Window State (KAN-47)
  isOver48Hours = false;
  isMediaConfirmedUnavailable = false;

  openModal(mediaUrl: string) {
    this.selectedMedia = mediaUrl;
  }

  closeModal() {
    this.selectedMedia = null;
  }

  ngOnInit() {
    this.sessionId = this.route.snapshot.paramMap.get('sessionId');
    this.machineId = this.route.snapshot.queryParamMap.get('machine');
    this.date = this.route.snapshot.queryParamMap.get('date');

    // date may be null for legacy QR codes — the service handles fallback to the old path
    if (this.sessionId && this.machineId) {
      this.fetchSessionMedia();
    }
  }

  fetchSessionMedia() {
    this.isLoading = true;
    
    this.userPortalService.getSessionMedia(this.machineId!, this.date!, this.sessionId!).subscribe({
      next: (data: any) => {
        if (data) {
          if (data.CutStyle) {
            this.cutStyle = data.CutStyle;
          }

          // Check if assets are marked deleted
          if (data.assetsDeleted === true) {
            this.isMediaConfirmedUnavailable = true;
          }

          // Calculate session age from authoritative timestamp or date
          let sessionTimestamp: Date | null = null;
          const rawTime = data.time || data.Time || data.timestamp || data.Timestamp || data.date || data.Date || this.date;
          if (rawTime) {
            if (typeof rawTime === 'string' && !isNaN(new Date(rawTime).getTime())) {
              sessionTimestamp = new Date(rawTime);
            } else if (typeof rawTime === 'object' && rawTime.toDate) {
              sessionTimestamp = rawTime.toDate();
            } else if (rawTime instanceof Date) {
              sessionTimestamp = rawTime;
            }
          }

          if (sessionTimestamp && !isNaN(sessionTimestamp.getTime())) {
            const elapsedHours = (Date.now() - sessionTimestamp.getTime()) / (3600 * 1000);
            if (elapsedHours > 48.0) {
              this.isOver48Hours = true;
            }
          }

          if (data.PrintUrl && !this.printUrl) {
            this.userPortalService.getFirebaseDownloadUrl(this.machineId!, this.date!, this.sessionId!, 'FinalPrint.jpg')
              .then(url => this.printUrl = url)
              .catch(err => {
                console.error('Error fetching secure PrintUrl:', err);
                if (err?.code === 'storage/object-not-found' || err?.code === 'storage/unknown') {
                  this.isMediaConfirmedUnavailable = true;
                }
              });
          }
          
          if (data.GifUrl && !this.gifUrl) {
            this.userPortalService.getFirebaseDownloadUrl(this.machineId!, this.date!, this.sessionId!, 'FinalPrint.gif')
              .then(url => this.gifUrl = url)
              .catch(err => {
                console.error('Error fetching secure GifUrl:', err);
                if (err?.code === 'storage/object-not-found' || err?.code === 'storage/unknown') {
                  this.isMediaConfirmedUnavailable = true;
                }
              });
          }
          
          if (!data.PrintUrl && !data.GifUrl && !data.printUrl && !data.gifUrl) {
            this.isMediaConfirmedUnavailable = true;
          }

          if (data.claimedByEmails) {
            const userEmail = this.auth.currentUser?.email || localStorage.getItem('userEmail');
            if (userEmail && data.claimedByEmails.includes(userEmail)) {
              this.isClaimed = true;
            }
          }
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error fetching session media:', error);
        this.isLoading = false;
      }
    });
  }

  async saveToGallery() {
    // date may be null for legacy QR codes — claimSession handles the fallback internally
    // currentUser.email is null for custom-token sign-ins, so fall back to localStorage
    const userEmail = this.auth.currentUser?.email || localStorage.getItem('userEmail');
    const uid = this.auth.currentUser?.uid;

    if (!this.machineId || !this.sessionId || !userEmail || !uid || this.isClaimed) return;

    this.isClaiming = true;
    try {
      await this.userPortalService.claimSession(
        this.machineId,
        this.date,
        this.sessionId,
        userEmail,
        uid,
        this.printUrl,
        this.gifUrl,
        this.cutStyle
      );
      this.isClaimed = true;
    } catch (error) {
      console.error('Failed to claim session:', error);
    } finally {
      this.isClaiming = false;
    }
  }

  async downloadMedia(url: string, type: 'photo' | 'gif') {
    if (!url) return;
    
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const fileName = `PhotoLavie-${this.sessionId}-${type}.${type === 'gif' ? 'gif' : 'jpg'}`;
      const mimeType = type === 'gif' ? 'image/gif' : 'image/jpeg';
      
      // Detect if device is mobile (iOS/Android)
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      
      // Try Web Share API ONLY for mobile devices (saves to photo gallery)
      if (isMobile && navigator.share && navigator.canShare) {
        const file = new File([blob], fileName, { type: mimeType });
        const shareData = { files: [file] };
        
        if (navigator.canShare(shareData)) {
          try {
            await navigator.share(shareData);
            return; // Success - user shared/saved the file
          } catch (shareError: any) {
            // User cancelled or share failed, fall through to download
            if (shareError.name !== 'AbortError') {
              console.log('Share failed, falling back to download:', shareError);
            }
          }
        }
      }
      
      // Standard browser download (for desktop or mobile fallback)
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error downloading file:', error);
      // Final fallback: open in new tab
      window.open(url, '_blank');
    }
  }

  photoOrientations: Record<string, string> = {};

  onImageLoad(event: Event, type: 'gif' | 'print') {
    const img = event.target as HTMLImageElement;
    if (!img.naturalWidth || !img.naturalHeight) return;

    const ratio = img.naturalWidth / img.naturalHeight;
    if (ratio > 1.1) {
      this.photoOrientations[type] = '6x4';
    } else if (ratio < 0.55) {
      this.photoOrientations[type] = 'strip';
    } else {
      this.photoOrientations[type] = '4x6';
    }
  }

}
